// Export Workflow — the standard follow-up question card (UX redesign slices C1 + B). Renders one
// follow-up item that carries options: title + one line of context, an amber "Answer to continue"
// while it is pending, 2-4 option cards (plain-language label, description, optional code chip),
// a wide "Not sure"-style escape row for `focus_composer` options, and an optional labelled input.
// The composer below stays usable: typing an answer instead of clicking still works. Items with
// no options and no input are NOT rendered here — EARChatUI keeps the plain-text bubble for them.
import React, { useId, useRef, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { iFollowupItem, iFollowupOption } from 'interfaces/iFollowupItem';

interface FollowupQuestionCardProps {
  item: iFollowupItem;
  /** Still waiting for an answer (false once answered by a click or by typing). */
  active: boolean;
  /** The option the user clicked, if any (drives the chosen / muted state). */
  selectedOptionId?: string | null;
  /** Blocks clicks while a turn is streaming. */
  disabled?: boolean;
  onSelect: (option: iFollowupOption) => void;
  onInputSubmit?: (value: string) => void;
  /** Pre-fills the labelled input (the return card after the Classify tool). */
  initialInputValue?: string;
  /** On an input card a `focus_composer` option focuses the card's own input (default) — the
   *  return card turns this off so "I have a Schedule B code instead" goes to the composer. */
  focusInputOnEscape?: boolean;
}

/** True when an item should render as a card rather than the plain-text bubble. */
export const isCardFollowup = (item: Partial<iFollowupItem>): boolean =>
  Boolean((item.options && item.options.length) || item.input);

const isEscapeOption = (option: iFollowupOption) => option.action === 'focus_composer';
const isClassifyAction = (option: iFollowupOption) => option.action === 'open_classify_tool';

const FollowupQuestionCard: React.FC<FollowupQuestionCardProps> = ({
  item,
  active,
  selectedOptionId,
  disabled = false,
  onSelect,
  onInputSubmit,
  initialInputValue = '',
  focusInputOnEscape = true,
}) => {
  const [inputValue, setInputValue] = useState(initialInputValue);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const options = item.options || [];
  // On an input card the input is the main path, so every option becomes a compact alternate
  // action below it — except "Open the Classify tool", which leads as the card's main action.
  const leadActions = item.input ? options.filter(isClassifyAction) : [];
  const answerOptions = item.input ? [] : options.filter((o) => !isEscapeOption(o));
  const escapeOptions = item.input
    ? options.filter((o) => !isClassifyAction(o))
    : options.filter(isEscapeOption);
  const steps = item.steps || [];
  // "I have a code after all" on an input card focuses the card's own input, not the composer.
  const select = (option: iFollowupOption) => {
    if (option.action === 'focus_composer' && focusInputOnEscape && item.input && inputRef.current) {
      inputRef.current.focus();
      return;
    }
    onSelect(option);
  };
  const locked = !active || disabled;
  // 2 or 4 options → two columns; 3 or 5+ (e.g. the 7 transaction modes) → three.
  const count = answerOptions.length;
  const columns = count <= 1 ? 1 : count === 2 || count === 4 ? 2 : 3;

  const optionClass = (option: iFollowupOption, base: string) => {
    const chosen = selectedOptionId === option.id;
    return `${base}${chosen ? ' is-selected' : ''}${!active && !chosen ? ' is-muted' : ''}`;
  };

  return (
    <div className={`export-followup-card${active ? '' : ' is-answered'}`}>
      <div className='export-followup-head'>
        <div className='export-followup-heading'>
          <div className='export-followup-title'>
            {item.title || item.question}
            {item.badge && <span className='export-followup-badge'>{item.badge}</span>}
          </div>
          {item.context && <div className='export-followup-context'>{item.context}</div>}
        </div>
        {active && item.required !== false && (
          <span className='export-followup-required'>
            <ErrorOutlineIcon aria-hidden='true' />
            Answer to continue
          </span>
        )}
      </div>

      {steps.length > 0 && (
        <ol className='export-followup-steps'>
          {steps.map((step, index) => (
            <li key={`${index}-${step.title}`} className='export-followup-step'>
              <span className='export-followup-step-number' aria-hidden='true'>
                {index + 1}
              </span>
              <span className='export-followup-step-body'>
                <span className='export-followup-step-title'>{step.title}</span>
                {step.description && (
                  <span className='export-followup-step-desc'>{step.description}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}

      {leadActions.length > 0 && (
        <div className='export-followup-lead-actions'>
          {leadActions.map((option) => (
            <button
              key={option.id}
              type='button'
              className='standard-workflow-primary-btn export-followup-lead-action'
              onClick={() => select(option)}
              disabled={locked}
              title={option.description || undefined}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {item.input && (
        <form
          className='export-followup-input'
          onSubmit={(event) => {
            event.preventDefault();
            const value = inputValue.trim();
            if (!value || locked || !onInputSubmit) return;
            onInputSubmit(value);
          }}
        >
          <label className='export-followup-input-label' htmlFor={inputId}>
            {item.input.label}
          </label>
          <div className='export-followup-input-control'>
            <input
              id={inputId}
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder={item.input.placeholder || undefined}
              disabled={locked}
            />
            <button
              type='submit'
              className='standard-workflow-primary-btn'
              disabled={locked || !inputValue.trim()}
            >
              {item.input.submit_label || 'Submit'}
            </button>
          </div>
          {item.input.hint && <span className='export-followup-note'>{item.input.hint}</span>}
        </form>
      )}

      {answerOptions.length > 0 && (
        <div
          className={`export-followup-options export-followup-options-${columns}${
            count > 4 ? ' is-compact' : ''
          }`}
          role='group'
          aria-label={item.title || item.question}
        >
          {answerOptions.map((option) => (
            <button
              key={option.id}
              type='button'
              className={optionClass(option, 'export-followup-option')}
              onClick={() => select(option)}
              disabled={locked}
              aria-pressed={selectedOptionId === option.id}
            >
              <span className='export-followup-option-label'>{option.label}</span>
              {option.description && (
                <span className='export-followup-option-desc'>{option.description}</span>
              )}
              {option.code && (
                <span className='export-followup-code'>
                  {option.code_label ? `${option.code_label} ` : ''}
                  {option.code}
                </span>
              )}
              {option.action === 'open_classify_tool' && (
                <span className='export-followup-tag'>Opens the Classify tool</span>
              )}
            </button>
          ))}
        </div>
      )}

      {item.input && escapeOptions.length > 0 ? (
        <div className='export-followup-alternate-actions'>
          {escapeOptions.map((option) => (
            <div key={option.id} className='export-followup-alternate-action'>
              <button
                type='button'
                className={optionClass(
                  option,
                  'standard-workflow-secondary-btn export-followup-alternate-btn',
                )}
                onClick={() => select(option)}
                disabled={locked}
              >
                {option.label}
              </button>
              {option.description && (
                <span className='export-followup-option-desc'>{option.description}</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        escapeOptions.map((option) => (
          <button
            key={option.id}
            type='button'
            className={optionClass(option, 'export-followup-option export-followup-escape')}
            onClick={() => select(option)}
            disabled={locked}
          >
            <span className='export-followup-option-label'>{option.label}</span>
            {option.description && (
              <span className='export-followup-option-desc'>{option.description}</span>
            )}
          </button>
        ))
      )}

      {item.note && <div className='export-followup-note export-followup-footnote'>{item.note}</div>}

      {active && !item.input && (
        <div className='export-followup-note'>
          {escapeOptions.length
            ? 'Or type your answer in the box below.'
            : 'You can also type your answer in the box below.'}
        </div>
      )}
    </div>
  );
};

export default FollowupQuestionCard;
